<template>
  <div class="exchange-rate">
    <!-- 页头 -->
    <header class="er-header">
      <div class="er-header__left">
        <h1 class="er-title">汇率管理</h1>
        <p class="er-subtitle">
          手动维护各币种对 CNY
          的汇率快照；页面汇总金额按快照折算，历史月报按月末日汇率计算（数值稳定）
        </p>
      </div>
      <div class="er-header__right">
        <el-tag v-if="effective" type="success" effect="plain" size="large">
          当前生效：{{ effective.date }} 版
        </el-tag>
      </div>
    </header>

    <main class="er-main">
      <!-- 录入区：一版汇率 -->
      <section class="er-card">
        <div class="er-card__head">
          <div class="er-card__title">录入汇率快照</div>
          <div class="er-card__desc">
            口径：1 单位外币 = 汇率 CNY（如 1 USD = 7.11 CNY）；CNY
            本身无需录入。同日同币种保存时覆盖旧值
          </div>
        </div>

        <div class="er-form">
          <div class="er-form__date">
            <span class="er-form__label">生效日期</span>
            <el-date-picker
              v-model="formDate"
              type="date"
              value-format="YYYY-MM-DD"
              placeholder="选择日期"
              :disabled-date="disableFuture"
              style="width: 180px"
              size="large"
            />
          </div>

          <div class="er-form__rows">
            <div v-for="(row, i) in formRows" :key="i" class="er-form__row">
              <el-select
                v-model="row.currency"
                placeholder="币种"
                size="large"
                style="width: 200px"
              >
                <el-option
                  v-for="c in currencyOptions"
                  :key="c.code"
                  :value="c.code"
                  :label="`${c.cname} ${c.code} ${c.symbol}`"
                  :disabled="isCurrencyUsed(c.code, i)"
                />
              </el-select>
              <span class="er-form__eq">1 {{ row.currency || "?" }} =</span>
              <el-input-number
                v-model="row.rate"
                :min="0.00000001"
                :step="0.01"
                :precision="6"
                :controls="false"
                size="large"
                placeholder="对 CNY 汇率"
                style="width: 220px"
              />
              <span class="er-form__unit">CNY</span>
              <el-button
                :icon="Delete"
                circle
                plain
                type="danger"
                size="small"
                @click="removeRow(i)"
              />
            </div>
          </div>

          <div class="er-form__actions">
            <el-button :icon="Plus" plain @click="addRow">添加币种</el-button>
            <el-button
              type="primary"
              :loading="saving"
              :disabled="!formDate || !validRows.length"
              @click="handleSave"
            >
              保存本版汇率
            </el-button>
          </div>
        </div>
      </section>

      <!-- 历史快照列表 -->
      <section class="er-card">
        <div class="er-card__head">
          <div class="er-card__title">历史快照</div>
          <div class="er-card__filter">
            <el-select
              v-model="filterCurrency"
              placeholder="全部币种"
              clearable
              size="default"
              style="width: 140px"
              @change="loadList"
            >
              <el-option
                v-for="c in currencyOptions"
                :key="c.code"
                :value="c.code"
                :label="c.code"
              />
            </el-select>
          </div>
        </div>

        <el-table
          v-loading="listLoading"
          :data="list"
          stripe
          style="width: 100%"
        >
          <el-table-column
            prop="rateDate"
            label="生效日期"
            width="130"
            sortable
          />
          <el-table-column prop="currency" label="币种" width="100" />
          <el-table-column label="汇率（对 CNY）" min-width="160">
            <template #default="{ row }">
              <span class="er-rate"
                >1 {{ row.currency }} = {{ row.rate }} CNY</span
              >
            </template>
          </el-table-column>
          <el-table-column prop="updaterId" label="录入人" width="160" />
          <el-table-column label="更新时间" min-width="180">
            <template #default="{ row }">
              {{ formatTime(row.updatetime || row.createtime) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="90" fixed="right">
            <template #default="{ row }">
              <el-popconfirm
                title="删除该条汇率快照？"
                confirm-button-text="删除"
                cancel-button-text="取消"
                @confirm="handleDelete(row)"
              >
                <template #reference>
                  <el-button type="danger" link size="small">删除</el-button>
                </template>
              </el-popconfirm>
            </template>
          </el-table-column>
          <template #empty>
            <el-empty description="暂无汇率快照，请先录入" :image-size="72" />
          </template>
        </el-table>
      </section>
    </main>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { ElMessage } from "element-plus";
import { Delete, Plus } from "@element-plus/icons-vue";
import {
  CurrencyOption,
  ExchangeRateItem,
  deleteExchangeRateApi,
  getCurrenciesApi,
  getEffectiveRatesApi,
  getExchangeRateListApi,
  saveExchangeRateApi,
} from "@/api/exchangeRateApi";

/** 录入行 */
interface FormRow {
  currency: string;
  rate: number | undefined;
}

const currencyOptions = ref<CurrencyOption[]>([]);
const effective = ref<{ date: string; rates: Record<string, number> } | null>(
  null
);
const list = ref<ExchangeRateItem[]>([]);
const listLoading = ref(false);
const saving = ref(false);

const formDate = ref<string>(today());
const formRows = ref<FormRow[]>([]);
const filterCurrency = ref<string>("");

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}

const disableFuture = (d: Date) => d.getTime() > Date.now();

/** 有效行（币种与汇率都已填） */
const validRows = computed(() =>
  formRows.value.filter((r) => r.currency && r.rate !== undefined && r.rate > 0)
);

/** 该币种是否已被其他行选中（避免同版重复） */
const isCurrencyUsed = (code: string, exceptIdx: number) =>
  formRows.value.some((r, i) => i !== exceptIdx && r.currency === code);

const addRow = (preset?: { currency: string; rate?: number }) => {
  formRows.value.push({
    currency: preset?.currency ?? "",
    rate: preset?.rate,
  });
};

const removeRow = (i: number) => {
  formRows.value.splice(i, 1);
};

/** 加载当前生效汇率，并预填录入区（方便在最新版基础上微调另存新版） */
const loadEffective = async () => {
  effective.value = await getEffectiveRatesApi();
  if (effective.value && Object.keys(effective.value.rates).length > 0) {
    formRows.value = Object.entries(effective.value.rates).map(
      ([currency, rate]) => ({ currency, rate })
    );
  } else if (formRows.value.length === 0) {
    addRow();
  }
};

const loadList = async () => {
  listLoading.value = true;
  try {
    list.value =
      (await getExchangeRateListApi(filterCurrency.value || undefined)) ?? [];
  } finally {
    listLoading.value = false;
  }
};

const handleSave = async () => {
  if (!formDate.value || !validRows.value.length) return;
  saving.value = true;
  try {
    const resp = await saveExchangeRateApi(
      formDate.value,
      validRows.value.map((r) => ({
        currency: r.currency,
        rate: Number(r.rate),
      }))
    );
    if (resp !== null) {
      ElMessage.success("汇率快照已保存");
      await loadEffective();
      await loadList();
    }
  } finally {
    saving.value = false;
  }
};

const handleDelete = async (row: ExchangeRateItem) => {
  const resp = await deleteExchangeRateApi(row.id);
  if (resp !== null) {
    ElMessage.success("已删除");
    await loadEffective();
    await loadList();
  }
};

const formatTime = (raw: string): string => {
  if (!raw) return "—";
  return raw.replace("T", " ").slice(0, 19);
};

onMounted(async () => {
  currencyOptions.value = (await getCurrenciesApi()) ?? [];
  await Promise.all([loadEffective(), loadList()]);
});
</script>

<style lang="less" scoped>
.exchange-rate {
  min-height: 100%;
  padding: 24px 28px;
  background: var(--p-bg, #f6f8fa);
}

.er-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    flex: 1;
  }
}

.er-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: var(--p-slate-800, #1e293b);
}

.er-subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--p-slate-500, #64748b);
  max-width: 720px;
  line-height: 1.6;
}

.er-main {
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 980px;
}

.er-card {
  background: #fff;
  border: 1px solid var(--p-slate-200, #e2e8f0);
  border-radius: 12px;
  padding: 20px 22px;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 15px;
    font-weight: 700;
    color: var(--p-slate-800, #1e293b);
  }

  &__desc {
    font-size: 12px;
    color: var(--p-slate-500, #64748b);
    margin-top: 4px;
  }
}

.er-form {
  &__date {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  &__label {
    font-size: 13px;
    color: var(--p-slate-600, #475569);
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: var(--p-slate-50, #f8fafc);
    border-radius: 8px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__eq {
    font-size: 13px;
    color: var(--p-slate-600, #475569);
    white-space: nowrap;
  }

  &__unit {
    font-size: 13px;
    color: var(--p-slate-600, #475569);
  }

  &__actions {
    display: flex;
    gap: 10px;
    margin-top: 14px;
  }
}

.er-rate {
  font-family: "JetBrains Mono", monospace;
  color: var(--p-cyan-deep, #0e7490);
}
</style>
